import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Container,
  Input,
  Button,
  VStack,
  Heading,
  useToast,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Spinner,
  Card,
  CardBody,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Text,
  FormHelperText,
  Alert,
  AlertIcon,
  HStack,
  Divider,
  Box,
  SimpleGrid,
  CheckboxGroup,
  Checkbox,
  Badge,
  Wrap,
  WrapItem,
  Icon,
  Switch,
  FormErrorMessage
} from '@chakra-ui/react'
import { ChevronRightIcon } from '@chakra-ui/icons'
import { FiGlobe, FiMapPin, FiStar } from 'react-icons/fi'
import { Link as RouterLink } from 'react-router-dom'
import apiClient from '../../../utils/apiClient'

const CreateSite = () => {
  const navigate = useNavigate()
  const toast = useToast()

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const cardBg = useColorModeValue('white', 'gray.700')

  // Get tenant ID from session storage
  const user = JSON.parse(sessionStorage.getItem('user'))
  const tenantId = user?.tenant_ids?.[0] || null

  // Form state
  const [site, setSite] = useState({
    name: '',
    domain: '',
    site_status: 'inactive',
    description: ''
  })
  const [localeSettings, setLocaleSettings] = useState({
    selectedLocales: [],
    defaultLocaleId: null,
    assignLocalesNow: false
  })
  const [loading, setLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [availableLocales, setAvailableLocales] = useState([])
  const [errors, setErrors] = useState({})

  useEffect(() => {
    const fetchAvailableLocales = async () => {
      try {
        const response = await apiClient(`/locales/${tenantId}`)
        setAvailableLocales(response)
      } catch (error) {
        toast({
          title: 'Error fetching locales',
          description: error.message,
          status: 'error',
          duration: 3000
        })
      } finally {
        setLoading(false)
      }
    }

    fetchAvailableLocales()
  }, [tenantId])

  const handleSiteChange = (e) => {
    const { name, value } = e.target
    setSite(prev => ({ ...prev, [name]: value }))
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const handleLocaleSelection = (selectedLocaleIds) => {
    setLocaleSettings(prev => ({
      ...prev,
      selectedLocales: selectedLocaleIds,
      // Reset default if it's not in the selected locales
      defaultLocaleId: selectedLocaleIds.includes(prev.defaultLocaleId) 
        ? prev.defaultLocaleId 
        : selectedLocaleIds[0] || null
    }))
  }

  const handleDefaultLocaleChange = (localeId) => {
    setLocaleSettings(prev => ({
      ...prev,
      defaultLocaleId: localeId
    }))
  }

  const validateForm = () => {
    const newErrors = {}
    
    if (!site.name.trim()) {
      newErrors.name = 'Site name is required'
    }
    
    if (!site.domain.trim()) {
      newErrors.domain = 'Domain is required'
    } else if (!/^[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.?[a-zA-Z]*$/.test(site.domain)) {
      newErrors.domain = 'Please enter a valid domain'
    }

    if (localeSettings.assignLocalesNow && localeSettings.selectedLocales.length === 0) {
      newErrors.locales = 'Please select at least one locale'
    }

    if (localeSettings.assignLocalesNow && localeSettings.selectedLocales.length > 0 && !localeSettings.defaultLocaleId) {
      newErrors.defaultLocale = 'Please select a default locale'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!validateForm() || isSubmitting) return
    
    setIsSubmitting(true)
    
    try {
      // Create the site first
      const sitePayload = {
        tenant_id: tenantId,
        name: site.name.trim(),
        domain: site.domain.trim(),
        site_status: site.site_status,
        description: site.description.trim(),
        default_locale_id: localeSettings.assignLocalesNow ? localeSettings.defaultLocaleId : null
      }

      const createdSite = await apiClient('/sites', {
        method: 'POST',
        body: JSON.stringify(sitePayload)
      })

      // If user chose to assign locales now, assign them
      if (localeSettings.assignLocalesNow && localeSettings.selectedLocales.length > 0) {
        const siteId = createdSite.id || createdSite.site_id

        // Assign all selected locales
        const localeAssignments = localeSettings.selectedLocales.map(localeId =>
          apiClient(`/sites/${tenantId}/${siteId}/locales`, {
            method: 'POST',
            body: JSON.stringify({
              locale_id: parseInt(localeId),
              is_default: localeId === localeSettings.defaultLocaleId,
              is_active: true
            })
          })
        )

        await Promise.all(localeAssignments)
      }

      toast({
        title: 'Success',
        description: `Site "${site.name}" created successfully${localeSettings.assignLocalesNow ? ' with locale assignments' : ''}`,
        status: 'success',
        duration: 5000
      })

      navigate('/sites')
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create site',
        status: 'error',
        duration: 5000
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const isFormValid = site.name.trim() && site.domain.trim() && Object.keys(errors).length === 0

  if (loading) {
    return (
      <Container maxW="container.lg" py={6}>
        <VStack align="center" justify="center" height="400px">
          <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
          <Text mt={4}>Loading available locales...</Text>
        </VStack>
      </Container>
    )
  }

  return (
    <Container maxW="container.lg" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/sites">Sites</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Create</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <form onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          {/* Page Header */}
          <Box>
            <HStack mb={2}>
              <Icon as={FiGlobe} color="blue.500" boxSize={6} />
              <Heading size="lg">Create New Site</Heading>
            </HStack>
            <Text color="gray.500">
              Create a new site and configure its multilingual settings. You can assign languages now or later.
            </Text>
          </Box>

          {/* Site Information Card */}
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <VStack spacing={6} align="stretch">
                <Box>
                  <Heading size="md" mb={4}>Site Information</Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <FormControl isRequired isInvalid={errors.name}>
                      <FormLabel>Site Name</FormLabel>
                      <Input
                        name="name"
                        value={site.name}
                        onChange={handleSiteChange}
                        placeholder="Enter site name"
                      />
                      {errors.name ? (
                        <FormErrorMessage>{errors.name}</FormErrorMessage>
                      ) : (
                        <FormHelperText>A descriptive name for your site</FormHelperText>
                      )}
                    </FormControl>

                    <FormControl isRequired isInvalid={errors.domain}>
                      <FormLabel>Domain</FormLabel>
                      <Input
                        name="domain"
                        value={site.domain}
                        onChange={handleSiteChange}
                        placeholder="example.com"
                      />
                      {errors.domain ? (
                        <FormErrorMessage>{errors.domain}</FormErrorMessage>
                      ) : (
                        <FormHelperText>The domain where this site will be accessed</FormHelperText>
                      )}
                    </FormControl>
                  </SimpleGrid>

                  <FormControl mt={6}>
                    <FormLabel>Status</FormLabel>
                    <Select name="site_status" value={site.site_status} onChange={handleSiteChange}>
                      <option value="inactive">Inactive</option>
                      <option value="active">Active</option>
                      <option value="maintenance">Maintenance</option>
                    </Select>
                    <FormHelperText>
                      Set to "Active" when the site is ready to receive traffic
                    </FormHelperText>
                  </FormControl>

                  <FormControl mt={6}>
                    <FormLabel>Description (Optional)</FormLabel>
                    <Textarea
                      name="description"
                      value={site.description}
                      onChange={handleSiteChange}
                      placeholder="Brief description of this site..."
                      rows={3}
                    />
                    <FormHelperText>
                      Optional description for internal reference
                    </FormHelperText>
                  </FormControl>
                </Box>
              </VStack>
            </CardBody>
          </Card>

          {/* Language Configuration Card */}
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <VStack spacing={6} align="stretch">
                <Box>
                  <HStack mb={4}>
                    <Icon as={FiMapPin} color="purple.500" />
                    <Heading size="md">Language Configuration</Heading>
                  </HStack>
                  
                  <FormControl>
                    <HStack justify="space-between" align="center">
                      <VStack align="start" spacing={1}>
                        <Text fontWeight="medium">Assign Languages Now</Text>
                        <Text fontSize="sm" color="gray.500">
                          Configure multilingual support during site creation
                        </Text>
                      </VStack>
                      <Switch
                        isChecked={localeSettings.assignLocalesNow}
                        onChange={(e) => setLocaleSettings(prev => ({
                          ...prev,
                          assignLocalesNow: e.target.checked,
                          selectedLocales: e.target.checked ? prev.selectedLocales : [],
                          defaultLocaleId: e.target.checked ? prev.defaultLocaleId : null
                        }))}
                        colorScheme="blue"
                        size="lg"
                      />
                    </HStack>
                  </FormControl>
                </Box>

                {localeSettings.assignLocalesNow && (
                  <>
                    <Divider />
                    
                    {availableLocales.length > 0 ? (
                      <VStack align="stretch" spacing={4}>
                        <FormControl isInvalid={errors.locales}>
                          <FormLabel>Available Languages ({localeSettings.selectedLocales.length} selected)</FormLabel>
                          <CheckboxGroup 
                            value={localeSettings.selectedLocales} 
                            onChange={handleLocaleSelection}
                          >
                            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                              {availableLocales.map(locale => (
                                <Card
                                  key={locale.id}
                                  variant="outline"
                                  size="sm"
                                  borderColor={localeSettings.selectedLocales.includes(locale.id.toString()) ? "blue.200" : borderColor}
                                  bg={localeSettings.selectedLocales.includes(locale.id.toString()) ? "blue.50" : "transparent"}
                                >
                                  <CardBody p={3}>
                                    <Checkbox
                                      value={locale.id.toString()}
                                      w="full"
                                    >
                                      <HStack justify="space-between" w="full">
                                        <VStack align="start" spacing={1}>
                                          <Text fontWeight="medium" fontSize="sm">
                                            {locale.name}
                                          </Text>
                                          <Badge size="sm" colorScheme="blue" variant="subtle">
                                            {locale.code}
                                          </Badge>
                                        </VStack>
                                      </HStack>
                                    </Checkbox>
                                  </CardBody>
                                </Card>
                              ))}
                            </SimpleGrid>
                          </CheckboxGroup>
                          {errors.locales && <FormErrorMessage>{errors.locales}</FormErrorMessage>}
                        </FormControl>

                        {localeSettings.selectedLocales.length > 0 && (
                          <FormControl isInvalid={errors.defaultLocale}>
                            <FormLabel>
                              <HStack>
                                <Icon as={FiStar} color="yellow.500" />
                                <Text>Default Language</Text>
                              </HStack>
                            </FormLabel>
                            <Select 
                              value={localeSettings.defaultLocaleId || ''} 
                              onChange={(e) => handleDefaultLocaleChange(e.target.value)}
                              placeholder="Select default language"
                            >
                              {availableLocales
                                .filter(locale => localeSettings.selectedLocales.includes(locale.id.toString()))
                                .map(locale => (
                                  <option key={locale.id} value={locale.id}>
                                    {locale.name} ({locale.code})
                                  </option>
                                ))
                              }
                            </Select>
                            {errors.defaultLocale ? (
                              <FormErrorMessage>{errors.defaultLocale}</FormErrorMessage>
                            ) : (
                              <FormHelperText>
                                This language will be used as fallback when content is not available in other languages
                              </FormHelperText>
                            )}
                          </FormControl>
                        )}

                        {/* Selected Languages Preview */}
                        {localeSettings.selectedLocales.length > 0 && (
                          <Box>
                            <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.600">
                              Selected Languages:
                            </Text>
                            <Wrap spacing={2}>
                              {availableLocales
                                .filter(locale => localeSettings.selectedLocales.includes(locale.id.toString()))
                                .map(locale => (
                                  <WrapItem key={locale.id}>
                                    <Badge 
                                      colorScheme={locale.id.toString() === localeSettings.defaultLocaleId ? 'yellow' : 'blue'} 
                                      variant="subtle"
                                    >
                                      {locale.name} ({locale.code})
                                      {locale.id.toString() === localeSettings.defaultLocaleId && ' ★'}
                                    </Badge>
                                  </WrapItem>
                                ))
                              }
                            </Wrap>
                          </Box>
                        )}
                      </VStack>
                    ) : (
                      <Alert status="info" borderRadius="md">
                        <AlertIcon />
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium">No Locales Available</Text>
                          <Text fontSize="sm">
                            Create some locales first, then you can assign them to sites.
                          </Text>
                        </VStack>
                      </Alert>
                    )}
                  </>
                )}

                {!localeSettings.assignLocalesNow && (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="medium">Languages can be configured later</Text>
                      <Text fontSize="sm">
                        You can assign and manage languages for this site after creation from the site edit page.
                      </Text>
                    </VStack>
                  </Alert>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Action Buttons */}
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardBody>
              <HStack spacing={4} justify="flex-end">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/sites')}
                  isDisabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isDisabled={!isFormValid}
                  isLoading={isSubmitting}
                  loadingText="Creating Site..."
                >
                  Create Site
                </Button>
              </HStack>
            </CardBody>
          </Card>
        </VStack>
      </form>
    </Container>
  )
}

export default CreateSite
